/**
 * SitePick front — かんたん診断（販売導線 v2 Phase 2。docs/18 §3）
 *
 * - フォーム（/diagnosis/）: サーバが描いた 1 フォーム（全問）を 1 問ずつのステップ表示に enhance する（progressive
 *   enhancement。JS 無しは全問を縦に並べて送信、JS 失敗でも `is-stepper` が付かないので本文は消えない）。
 *   - 業態（Q1b）は選ばれた業種の group だけ見せる。候補が無い業種なら飛ばす。
 *   - 複数選択は `data-sp-max` 個まで（超えたら残りを disabled）。必須設問は未回答だと次へ進めない。
 *   - 計測: view_diagnosis（表示）/ diagnosis_start（最初の回答）/ diagnosis_step（設問の確定ごと）/
 *     diagnosis_complete（送信直前。sessionStorage でリロード再送を抑止）。回答の値は term slug / allowlist だけ。
 * - 結果（/diagnosis/result/）: view_diagnosis_result を 1 回/pageview（data-sp-* を読むだけ。再計算はサーバ）。
 *   結果カードの view_item_list / select_item は list-tracking.js（item_list_id=diagnosis）が既存どおり送る。
 *
 * 価格・在庫・スコアはここで扱わない（CLAUDE.md §11）。
 */

import {
  viewDiagnosis,
  diagnosisStart,
  diagnosisStep,
  diagnosisComplete,
  viewDiagnosisResult,
} from "./analytics.js";

const COMPLETE_KEY = "sp_dx_complete";

/**
 * sessionStorage（使えなければ何もしない）。
 *
 * @param {string} key
 * @returns {string|null}
 */
function storageGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 * @returns {void}
 */
function storageSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* private mode 等 */
  }
}

/**
 * 設問ブロックの状態。
 *
 * @typedef {Object} Question
 * @property {HTMLFieldSetElement} el
 * @property {string} id       industry / market / purpose / impression / feature / scale
 * @property {string} step     "1" / "1b" / "2" ...
 * @property {string} type     single / multi
 * @property {boolean} required
 * @property {number} max      multi の上限（0 = 無制限）
 */

/**
 * @param {HTMLFieldSetElement} el
 * @returns {Question}
 */
function readQuestion(el) {
  return {
    el,
    id: el.getAttribute("data-sp-q") || "",
    step: el.getAttribute("data-sp-step") || "",
    type: el.getAttribute("data-sp-type") || "single",
    required: el.getAttribute("data-sp-required") === "1",
    max: Number.parseInt(el.getAttribute("data-sp-max") || "0", 10) || 0,
  };
}

/**
 * 設問の回答値（single は 1 つ、multi は配列）。業態は選ばれた業種の group にある input だけ見る。
 *
 * @param {Question} q
 * @returns {string[]}
 */
function selectedValues(q) {
  return Array.from(q.el.querySelectorAll("input:checked"))
    .filter((input) => !input.closest(".js-sp-market-group[hidden]"))
    .map((input) => input.value)
    .filter((v) => v !== "");
}

/**
 * フォームの enhance。
 *
 * @param {HTMLFormElement} form
 * @returns {void}
 */
function initForm(form) {
  const entry = form.getAttribute("data-sp-diagnosis-entry") || "direct";
  const questions = Array.from(form.querySelectorAll("fieldset[data-sp-q]")).map(readQuestion);
  if (questions.length === 0) {
    return;
  }
  const industryQ = questions.find((q) => q.id === "industry");
  const marketQ = questions.find((q) => q.id === "market");
  const total = Number.parseInt(form.getAttribute("data-sp-diagnosis-total") || "", 10) || questions.length;
  const progress = form.querySelector(".js-sp-diagnosis-progress");
  const prevBtn = form.querySelector(".js-sp-diagnosis-prev");
  const nextBtn = form.querySelector(".js-sp-diagnosis-next");
  const submitBtn = form.querySelector(".js-sp-diagnosis-submit");
  const prefilled = industryQ ? selectedValues(industryQ)[0] || "" : "";

  viewDiagnosis(entry, prefilled);

  let started = false;
  const firedSteps = new Set();


  /** 業種に業態の候補があるか（無ければ Q1b を飛ばす）。 */
  const marketGroups = marketQ ? Array.from(marketQ.el.querySelectorAll(".js-sp-market-group")) : [];
  const syncMarket = () => {
    if (!marketQ || !industryQ) {
      return false;
    }
    const industry = selectedValues(industryQ)[0] || "";
    let visible = false;
    marketGroups.forEach((group) => {
      const match = group.getAttribute("data-sp-industry") === industry;
      group.hidden = !match;
      // 隠した group の選択は送らない（業種を変えたら業態もリセット）。
      if (!match) {
        group.querySelectorAll("input:checked").forEach((input) => {
          input.checked = false;
        });
      }
      if (match) {
        visible = true;
      }
    });
    return visible;
  };

  /** 表示対象の設問（業態は候補のある業種のときだけ）。 */
  const visibleQuestions = () => questions.filter((q) => q.id !== "market" || syncMarket());

  const enforceMax = (q) => {
    if (q.type !== "multi" || q.max <= 0) {
      return;
    }
    const boxes = Array.from(q.el.querySelectorAll("input[type=checkbox]"));
    const checked = boxes.filter((b) => b.checked).length;
    boxes.forEach((b) => {
      b.disabled = !b.checked && checked >= q.max;
      const opt = b.closest(".p-diagnosis__opt");
      if (opt) {
        opt.classList.toggle("is-disabled", b.disabled);
      }
    });
  };

  const isAnswered = (q) => !q.required || selectedValues(q).length > 0;

  let index = 0;
  const render = () => {
    const list = visibleQuestions();
    index = Math.max(0, Math.min(index, list.length - 1));
    const current = list[index];
    questions.forEach((q) => {
      q.el.classList.toggle("is-current", q === current);
    });
    const last = index === list.length - 1;
    if (prevBtn) {
      prevBtn.hidden = index === 0;
    }
    if (nextBtn) {
      nextBtn.hidden = last;
      nextBtn.disabled = !isAnswered(current);
    }
    if (submitBtn) {
      submitBtn.hidden = !last;
      submitBtn.disabled = last && !isAnswered(current);
    }
    if (progress) {
      const n = current.id === "market" ? 1 : Number.parseInt(current.step, 10) || index + 1;
      progress.textContent = `${n} / ${total}`;
      progress.hidden = false;
    }
    if (started && current.el !== document.activeElement && !current.el.contains(document.activeElement)) {
      current.el.setAttribute("tabindex", "-1");
      current.el.focus({ preventScroll: true });
      current.el.scrollIntoView({ block: "start", behavior: "auto" });
    }
  };

  const fireStep = (q) => {
    if (!q || firedSteps.has(q.step)) {
      return;
    }
    firedSteps.add(q.step);
    diagnosisStep(q.step, entry);
  };

  form.addEventListener("change", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    const q = questions.find((item) => item.el.contains(input));
    if (!q) {
      return;
    }
    if (!started) {
      started = true;
      diagnosisStart(entry);
    }
    enforceMax(q);
    if (q.id === "industry") {
      syncMarket();
    }
    if (q.type === "single") {
      // 単一選択（業態の「あてはまるものがない」も含む）は選んだ時点で確定（1 タップで次へ）。
      fireStep(q);
      const list = visibleQuestions();
      if (index < list.length - 1 && list[index] === q) {
        index += 1;
      }
    }
    render();
  });

  if (nextBtn) {
    nextBtn.addEventListener("click", () => {
      const list = visibleQuestions();
      const current = list[index];
      if (!isAnswered(current)) {
        current.el.classList.add("has-error");
        return;
      }
      current.el.classList.remove("has-error");
      fireStep(current);
      if (index < list.length - 1) {
        index += 1;
      }
      render();
    });
  }
  if (prevBtn) {
    prevBtn.addEventListener("click", () => {
      index = Math.max(0, index - 1);
      render();
    });
  }

  form.addEventListener("submit", (event) => {
    const list = visibleQuestions();
    const missing = list.find((q) => !isAnswered(q));
    if (missing) {
      event.preventDefault();
      index = list.indexOf(missing);
      render();
      missing.el.classList.add("has-error");
      return;
    }
    fireStep(list[list.length - 1]);
    const answers = {};
    questions.forEach((q) => {
      answers[q.id] = selectedValues(q);
    });
    const key = JSON.stringify([entry, answers]);
    if (storageGet(COMPLETE_KEY) !== key) {
      diagnosisComplete(entry, answers);
      storageSet(COMPLETE_KEY, key);
    }
  });

  questions.forEach(enforceMax);
  // 既に回答のある設問（プリフィル / 条件を変える）は飛ばして最初の未回答から始める（業種ページから来たら業態から）。
  const initial = visibleQuestions();
  const firstUnanswered = initial.findIndex((q) => selectedValues(q).length === 0);
  index = firstUnanswered === -1 ? 0 : firstUnanswered;
  form.classList.add("is-stepper");
  render();
}

/**
 * 結果ページの計測（表示で 1 回/pageview）。
 *
 * @param {HTMLElement} root
 * @returns {void}
 */
function initResult(root) {
  const guardKey = `sp_dxr_${window.location.pathname}${window.location.search}`;
  if (storageGet(guardKey)) {
    return;
  }
  storageSet(guardKey, "1");
  viewDiagnosisResult({
    result_count: Number.parseInt(root.getAttribute("data-sp-result-count") || "0", 10) || 0,
    diagnosis_entry: root.getAttribute("data-sp-diagnosis-entry") || "direct",
    item_id: root.getAttribute("data-sp-top-item") || "",
  });
}

/**
 * @returns {void}
 */
export function initDiagnosis() {
  const form = document.querySelector("form[data-sp-diagnosis=\"form\"]");
  if (form instanceof HTMLFormElement) {
    initForm(form);
  }
  const result = document.querySelector("[data-sp-diagnosis=\"result\"]");
  if (result instanceof HTMLElement) {
    initResult(result);
  }
}
